import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  memo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Image as ImageIcon,
  Heart,
  Download,
  MessageSquare,
  Send,
  Loader,
  Camera,
} from "lucide-react";
import PhotoModal from "./PhotoModal";
import OptimizedImage from "./OptimizedImage";
import RegisterPrompt from "./RegisterPrompt";
import PhotographerInvite from "./PhotographerInvite";
import CommentModal from "./CommentModal";
import SkeletonPhoto from "./SkeletonPhoto";
import { prefetchImages, getOptimalQuality } from "../utils/imageConfig";

// ===== CONSTANTES DE PAGINACIÓN =====
const INITIAL_PHOTOS_COUNT = 12; // Fotos iniciales a mostrar
const PHOTOS_PER_PAGE = 8; // Fotos a cargar por página adicional

// ===== COMPONENTE MEMORIZADO PARA CADA FOTO =====
// Evita re-renders innecesarios cuando otras fotos cambian
const PhotoItem = memo(function PhotoItem({
  photo,
  index,
  sizeClasses,
  optimalSize,
  size,
  onOpenModal,
  onLike,
  onComment,
  onDownload,
}) {
  return (
    <motion.div
      className={`${sizeClasses} relative overflow-hidden cursor-pointer group bg-neutral-light`}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ delay: Math.min(index * 0.02, 0.3), duration: 0.3 }}
      onClick={() => onOpenModal(photo)}
    >
      {/* Imagen con lazy loading nativo */}
      <OptimizedImage
        photo={photo}
        size={size === "large" ? "medium" : optimalSize}
        alt={photo.message || "Foto del evento"}
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        priority={index < 6}
      />

      {/* Overlay con información */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 sm:transition-opacity duration-300 flex flex-col justify-between p-3 sm:p-4">
        {/* Info superior - Usuario */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-primary text-xs font-bold shadow-soft">
            {photo.uploader?.charAt(0)?.toUpperCase()}
          </div>
          <span className="text-white font-sans font-semibold text-sm drop-shadow-lg">
            {photo.uploader}
          </span>
        </div>

        {/* Info inferior - Stats y mensaje */}
        <div className="space-y-2">
          {photo.message && size !== "normal" && (
            <p className="text-white text-xs sm:text-sm font-sans line-clamp-2 drop-shadow-lg">
              {photo.message}
            </p>
          )}

          <div className="flex items-center gap-3 sm:gap-4">
            {/* Likes */}
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                onLike(photo.id);
              }}
              className="flex items-center gap-1.5 text-white hover:scale-110 transition-transform"
              whileTap={{ scale: 0.9 }}
            >
              <Heart
                className="w-4 h-4 sm:w-5 sm:h-5 drop-shadow-lg"
                fill={photo.likes > 0 ? "currentColor" : "none"}
              />
              <span className="text-sm sm:text-base font-bold font-sans drop-shadow-lg">
                {photo.likes || 0}
              </span>
            </motion.button>

            {/* Comments */}
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                onComment(photo);
              }}
              className="flex items-center gap-1.5 text-white hover:scale-110 transition-transform"
              whileTap={{ scale: 0.9 }}
            >
              <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 drop-shadow-lg" />
              <span className="text-sm sm:text-base font-bold font-sans drop-shadow-lg">
                {Array.isArray(photo.comments) ? photo.comments.length : 0}
              </span>
            </motion.button>

            {/* Download */}
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                onDownload(photo);
              }}
              className="ml-auto text-white hover:scale-110 transition-transform"
              whileTap={{ scale: 0.9 }}
            >
              <Download className="w-4 h-4 sm:w-5 sm:h-5 drop-shadow-lg" />
            </motion.button>
          </div>
        </div>
      </div>

      {/* Badge de fecha */}
      <div className="absolute top-2 right-2 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-full text-white text-[10px] sm:text-xs font-sans font-medium">
        {new Date(photo.created_at).toLocaleDateString("es-MX", {
          day: "numeric",
          month: "short",
        })}
      </div>
    </motion.div>
  );
});

// Comparador personalizado para memo - solo re-renderiza si cambian datos relevantes
PhotoItem.displayName = "PhotoItem";

const PhotoGallery = ({
  photos = [],
  onLike = () => {},
  onRequestAuth = () => {},
  onModalOpenChange = () => {},
}) => {
  const [localPhotos, setLocalPhotos] = useState(photos);
  const [commentInputs, setCommentInputs] = useState({});
  const [commenting, setCommenting] = useState({});
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingImages, setLoadingImages] = useState({});
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const [showPhotographerInvite, setShowPhotographerInvite] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [photoToComment, setPhotoToComment] = useState(null);
  const [pullStartY, setPullStartY] = useState(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(photos.length === 0);

  // ===== ESTADOS DE PAGINACIÓN =====
  const [visibleCount, setVisibleCount] = useState(INITIAL_PHOTOS_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);

  console.log("Fotos recibidas en PhotoGallery:", photos);

  // Agregar log para verificar localPhotos
  console.log("Estado localPhotos en PhotoGallery:", localPhotos);

  // ===== INTERSECTION OBSERVER PARA INFINITE SCROLL =====
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          !isLoadingMore &&
          visibleCount < localPhotos.length
        ) {
          setIsLoadingMore(true);
          // Simular delay pequeño para UX suave
          setTimeout(() => {
            setVisibleCount((prev) =>
              Math.min(prev + PHOTOS_PER_PAGE, localPhotos.length),
            );
            setIsLoadingMore(false);
          }, 300);
        }
      },
      {
        rootMargin: "200px", // Cargar antes de llegar al final
        threshold: 0.1,
      },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [visibleCount, localPhotos.length, isLoadingMore]);

  // Resetear visibleCount cuando cambian las fotos
  useEffect(() => {
    setVisibleCount(INITIAL_PHOTOS_COUNT);
  }, [photos.length]);

  // ===== SINCRONIZACIÓN OPTIMIZADA DE ESTADO (única fuente de verdad) =====
  // Solo actualiza localPhotos cuando photos realmente cambie
  const photosRef = useRef(photos);
  const photoIdsRef = useRef(new Set());

  useEffect(() => {
    // Validar que photos sea un array
    if (!Array.isArray(photos)) {
      console.error("Photos no es un array:", photos);
      setLocalPhotos([]);
      return;
    }

    // Crear set de IDs actuales para comparación rápida
    const currentIds = new Set(photos.map((p) => p.id));
    const prevIds = photoIdsRef.current;

    // Detectar si solo se agregaron fotos nuevas (sin eliminar/modificar existentes)
    const onlyAdditions =
      photos.length > prevIds.size &&
      [...prevIds].every((id) => currentIds.has(id));

    // Si no hay cambios reales, salir
    if (
      currentIds.size === prevIds.size &&
      [...currentIds].every((id) => prevIds.has(id))
    ) {
      return;
    }

    photosRef.current = photos;
    photoIdsRef.current = currentIds;

    if (photos.length > 0) {
      setIsInitialLoading(false);
      // Prefetch solo de las primeras fotos visibles
      prefetchImages(photos, INITIAL_PHOTOS_COUNT);
    }

    // Si solo se agregaron fotos nuevas, hacer merge eficiente
    if (onlyAdditions && prevIds.size > 0) {
      setLocalPhotos((prevPhotos) => {
        const prevPhotosMap = new Map(prevPhotos.map((p) => [p.id, p]));

        // Solo crear nuevos objetos para fotos que no existían
        return photos.map((photo) => {
          const existing = prevPhotosMap.get(photo.id);
          if (existing) {
            return existing; // Reutilizar referencia existente
          }
          // Nueva foto
          return {
            ...photo,
            likes: photo.likes || 0,
            comments: photo.comments || [],
            commentsLoaded: false,
          };
        });
      });
    } else {
      // Cambio completo: reconstruir con preservación de comentarios
      setLocalPhotos((prevPhotos) => {
        const prevPhotosMap = new Map(prevPhotos.map((p) => [p.id, p]));

        return photos.map((photo) => {
          const existing = prevPhotosMap.get(photo.id);
          return {
            ...photo,
            likes: photo.likes || 0,
            comments: existing?.commentsLoaded
              ? existing.comments
              : photo.comments || [],
            commentsLoaded: existing?.commentsLoaded || false,
          };
        });
      });
    }
  }, [photos]);

  // Efecto para recuperar comentarios SOLO de fotos visibles (optimización)
  useEffect(() => {
    const fetchCommentsForVisiblePhotos = async () => {
      if (!localPhotos.length) return;

      // Solo cargar comentarios de las fotos actualmente visibles
      const visiblePhotos = localPhotos.slice(0, visibleCount);

      try {
        const updatedPhotos = await Promise.all(
          visiblePhotos.map(async (photo) => {
            // Si ya tiene comentarios cargados, no volver a cargar
            if (photo.commentsLoaded) return photo;

            const response = await fetch(
              `/api/comments.php?photo_id=${photo.id}&limit=5`,
            );
            if (!response.ok) {
              throw new Error(`Error del servidor: ${response.statusText}`);
            }
            const data = await response.json();
            // Soportar nuevo formato con paginación o formato legacy (array directo)
            const comments = Array.isArray(data) ? data : data.comments || [];
            return { ...photo, comments: comments, commentsLoaded: true };
          }),
        );

        // Actualizar solo las fotos que cambiaron
        setLocalPhotos((prev) => {
          const newPhotos = [...prev];
          updatedPhotos.forEach((updated, idx) => {
            if (idx < newPhotos.length) {
              newPhotos[idx] = updated;
            }
          });
          return newPhotos;
        });
      } catch (error) {
        console.error("Error al recuperar los comentarios:", error);
      }
    };

    fetchCommentsForVisiblePhotos();
  }, [visibleCount]); // Solo re-ejecutar cuando cambia visibleCount

  const handleAddComment = async (photoId, event) => {
    event.preventDefault(); // Prevenir la recarga de la página

    // Validar que el usuario esté registrado
    const userName = localStorage.getItem("uploaderName");
    if (!userName || userName.trim() === "") {
      setShowRegisterPrompt(true);
      return;
    }

    const text = commentInputs[photoId]?.trim();
    if (!text) {
      alert("El comentario no puede estar vacío.");
      return;
    }

    if (!photoId || typeof photoId !== "number") {
      console.error("ID de foto inválido:", photoId);
      alert("Error interno. Por favor, recarga la página.");
      return;
    }

    setCommenting((prev) => ({ ...prev, [photoId]: true }));

    // Obtener o generar un ID único de dispositivo
    let deviceId = localStorage.getItem("deviceId");
    if (!deviceId) {
      deviceId =
        "device_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("deviceId", deviceId);
    }

    try {
      const response = await fetch(`/api/comments.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          photo_id: photoId,
          user_name: userName,
          comment: text,
        }),
      });

      console.log("Datos enviados:", {
        photo_id: photoId,
        user_name: userName,
        comment: text,
      });

      if (!response.ok) {
        throw new Error(`Error del servidor: ${response.statusText}`);
      }

      const data = await response.json();
      setLocalPhotos((prev) =>
        prev.map((photo) =>
          photo.id === photoId
            ? { ...photo, comments: [...photo.comments, data.comment] }
            : photo,
        ),
      );

      // Limpiar el input después de enviar
      setCommentInputs((prev) => ({
        ...prev,
        [photoId]: "",
      }));
    } catch (error) {
      console.error("Error al enviar el comentario:", error);
    } finally {
      setCommenting((prev) => ({ ...prev, [photoId]: false }));
    }
  };

  // Ajustar manejo de errores para evitar bucles
  const handleImageError = (e) => {
    e.stopPropagation(); // Detener propagación del evento
    e.target.onerror = null; // Evitar bucles infinitos
    e.target.src =
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23ddd' width='400' height='300'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='24' dy='150' dx='120'%3EImagen no disponible%3C/text%3E%3C/svg%3E"; // Imagen de respaldo SVG
    console.error("Error al cargar la imagen:", e.target.src);
  };

  const handleLikeClick = async (photoId, updatedLikes = null) => {
    // Si ya tenemos el conteo actualizado desde el modal, solo actualizamos el estado
    if (updatedLikes !== null) {
      setLocalPhotos((prev) =>
        prev.map((photo) =>
          photo.id === photoId ? { ...photo, likes: updatedLikes } : photo,
        ),
      );
      onLike(photoId);
      return;
    }

    // Verificar si el usuario está registrado
    const userName = localStorage.getItem("uploaderName");
    if (!userName) {
      setShowPhotographerInvite(true);
      return;
    }

    try {
      // Obtener o generar un ID único de dispositivo
      let deviceId = localStorage.getItem("deviceId");
      if (!deviceId) {
        deviceId =
          "device_" +
          Date.now() +
          "_" +
          Math.random().toString(36).substr(2, 9);
        localStorage.setItem("deviceId", deviceId);
      }

      const userName = localStorage.getItem("uploaderName");

      console.log("Enviando datos al backend:", {
        photo_id: photoId,
        user_id: deviceId,
        user_name: userName,
      });
      const response = await fetch(`/api/likes.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ photo_id: photoId, user_id: deviceId }),
      });

      if (!response.ok) {
        throw new Error(`Error del servidor: ${response.statusText}`);
      }

      const data = await response.json();
      setLocalPhotos((prev) =>
        prev.map((photo) =>
          photo.id === photoId ? { ...photo, likes: data.total_likes } : photo,
        ),
      );
    } catch (error) {
      console.error("Error al registrar el like:", error);
      alert("No se pudo registrar el like. Por favor, inténtalo nuevamente.");
    }

    onLike(photoId);
  };

  // Actualizar manejo de URLs y errores
  const normalizeUrl = (url) => {
    try {
      const baseUrl = window.location.origin;
      if (!url.startsWith("http")) {
        // Asegurar que las URLs relativas se conviertan en absolutas
        return new URL(url, baseUrl).href;
      }
      return encodeURI(url);
    } catch (error) {
      console.error("Error al normalizar URL:", url, error);
      return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23ddd' width='400' height='300'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='24' dy='150' dx='120'%3EImagen no disponible%3C/text%3E%3C/svg%3E";
    }
  };

  // Formatear la fecha antes de mostrarla
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "Fecha inválida" : date.toLocaleString();
  };

  // Mezclar mock comentarios en fotos locales
  const enrichedPhotos = localPhotos.map((photo) => ({
    ...photo,
    comments: photo.comments
      ? photo.comments
          .filter(
            (comment) =>
              comment &&
              typeof comment === "object" &&
              (comment.timestamp || comment.created_at) &&
              (comment.author || comment.text),
          )
          .map((comment) => {
            return {
              ...comment,
              author: comment.author || `Usuario ${comment.user_id}`,
              text: comment.text || comment.comment,
              timestamp: comment.timestamp || comment.created_at,
              formattedDate: formatDate(
                comment.timestamp || comment.created_at,
              ),
            };
          })
      : [],
  }));

  // ===== FOTOS VISIBLES PARA RENDERIZADO (PAGINACIÓN VIRTUAL) =====
  const visiblePhotos = useMemo(() => {
    return enrichedPhotos.slice(0, visibleCount);
  }, [enrichedPhotos, visibleCount]);

  const hasMorePhotos = visibleCount < enrichedPhotos.length;

  const openModal = (photo) => {
    setSelectedPhoto(photo);
    setIsModalOpen(true);
    onModalOpenChange(true);
    // Cerrar el modal de comentarios si está abierto
    setShowCommentModal(false);
    setPhotoToComment(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    onModalOpenChange(false);
    setTimeout(() => setSelectedPhoto(null), 300);
  };

  const handlePullStart = (e) => {
    if (window.scrollY === 0) {
      setPullStartY(e.touches[0].clientY);
    }
  };

  const handlePullMove = (e) => {
    if (pullStartY > 0 && window.scrollY === 0) {
      const currentY = e.touches[0].clientY;
      const distance = currentY - pullStartY;
      if (distance > 0 && distance < 150) {
        setPullDistance(distance);
      }
    }
  };

  const handlePullEnd = async () => {
    const PULL_THRESHOLD = 80;
    if (pullDistance > PULL_THRESHOLD) {
      setIsRefreshing(true);
      if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate(30);
      }
      // Recargar fotos
      try {
        const response = await fetch(`/api/photo.php?event_id=${eventId}`);
        if (response.ok) {
          const freshPhotos = await response.json();
          setLocalPhotos(freshPhotos);
        }
      } catch (error) {
        console.error("Error al actualizar:", error);
      }
      setTimeout(() => setIsRefreshing(false), 500);
    }
    setPullDistance(0);
    setPullStartY(0);
  };

  const handleNextPhoto = useCallback(() => {
    const currentIndex = sortedPhotos.findIndex(
      (p) => p.id === selectedPhoto?.id,
    );
    if (currentIndex < sortedPhotos.length - 1) {
      setSelectedPhoto(sortedPhotos[currentIndex + 1]);
    }
  }, [sortedPhotos, selectedPhoto?.id]);

  const handlePrevPhoto = useCallback(() => {
    const currentIndex = sortedPhotos.findIndex(
      (p) => p.id === selectedPhoto?.id,
    );
    if (currentIndex > 0) {
      setSelectedPhoto(sortedPhotos[currentIndex - 1]);
    }
  }, [sortedPhotos, selectedPhoto?.id]);

  const handleDownload = useCallback(async (photo) => {
    try {
      const response = await fetch(photo.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `foto-${photo.id}.jpg`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al descargar:", error);
    }
  }, []);

  const handleCommentClickForItem = useCallback((photo) => {
    const uploaderName = localStorage.getItem("uploaderName");
    if (!uploaderName) {
      setShowRegisterPrompt(true);
    } else {
      setPhotoToComment(photo);
      setShowCommentModal(true);
    }
  }, []);

  const handleCommentClick = (e, photo) => {
    e.stopPropagation();
    handleCommentClickForItem(photo);
  };

  const handleCommentSubmit = async (commentText) => {
    if (!photoToComment) return;

    const userName = localStorage.getItem("uploaderName");
    const photoId = photoToComment.id;

    // === OPTIMISTIC UPDATE: Agregar comentario inmediatamente ===
    const tempId = `temp_${Date.now()}`;
    const optimisticComment = {
      id: tempId,
      author: userName,
      text: commentText,
      timestamp: new Date().toISOString(),
    };

    // Actualizar UI inmediatamente
    setLocalPhotos((prev) =>
      prev.map((p) =>
        p.id === photoId
          ? { ...p, comments: [...(p.comments || []), optimisticComment] }
          : p,
      ),
    );

    try {
      const response = await fetch("/api/comments.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          photo_id: photoId,
          user_name: userName,
          comment: commentText,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Comentario enviado exitosamente:", result);

        // Reemplazar el comentario temporal con el real del servidor
        if (result.comment) {
          setLocalPhotos((prev) =>
            prev.map((p) =>
              p.id === photoId
                ? {
                    ...p,
                    comments: p.comments.map((c) =>
                      c.id === tempId ? { ...result.comment } : c,
                    ),
                  }
                : p,
            ),
          );
        }
      } else {
        // Si falla, remover el comentario optimista
        console.error("Error en respuesta del servidor");
        setLocalPhotos((prev) =>
          prev.map((p) =>
            p.id === photoId
              ? {
                  ...p,
                  comments: (p.comments || []).filter((c) => c.id !== tempId),
                }
              : p,
          ),
        );
      }
    } catch (error) {
      console.error("Error al enviar comentario:", error);
      // Remover comentario optimista en caso de error
      setLocalPhotos((prev) =>
        prev.map((p) =>
          p.id === photoId
            ? {
                ...p,
                comments: (p.comments || []).filter((c) => c.id !== tempId),
              }
            : p,
        ),
      );
    }
  };

  // Ordenar fotos por fecha de creación (created_at) en orden descendente
  const sortedPhotos = [...localPhotos].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );

  const currentIndex = sortedPhotos.findIndex(
    (p) => p.id === selectedPhoto?.id,
  );

  // Función para determinar el tamaño de cada foto (estilo Instagram Explore)
  const getPhotoSize = (index) => {
    // Patrón que se repite cada 19 fotos (inspirado en Instagram)
    const pattern = index % 19;

    // Patrones de tamaño:
    // 'large' = 2x2, 'tall' = 1x2, 'wide' = 2x1, 'normal' = 1x1
    const layouts = [
      "large", // 0: Grande 2x2
      "normal", // 1: Normal
      "normal", // 2: Normal
      "tall", // 3: Alto 1x2
      "normal", // 4: Normal
      "normal", // 5: Normal
      "wide", // 6: Ancho 2x1
      "normal", // 7: Normal
      "normal", // 8: Normal
      "normal", // 9: Normal
      "large", // 10: Grande 2x2
      "normal", // 11: Normal
      "tall", // 12: Alto 1x2
      "normal", // 13: Normal
      "wide", // 14: Ancho 2x1
      "normal", // 15: Normal
      "normal", // 16: Normal
      "tall", // 17: Alto 1x2
      "normal", // 18: Normal
    ];

    return layouts[pattern];
  };

  // Clases para cada tamaño (responsive)
  const getSizeClasses = (size) => {
    switch (size) {
      case "large":
        return "col-span-1 row-span-2 sm:col-span-2 sm:row-span-2"; // 2x2 en desktop
      case "tall":
        return "col-span-1 row-span-2"; // 1x2
      case "wide":
        return "col-span-1 sm:col-span-2 row-span-1"; // 2x1 en desktop
      default:
        return "col-span-1 row-span-1"; // 1x1
    }
  };

  return (
    <>
      {/* Indicador de pull-to-refresh */}
      {pullDistance > 0 && (
        <div
          className="fixed top-0 left-0 right-0 flex justify-center items-center z-50 transition-all"
          style={{
            height: `${Math.min(pullDistance, 80)}px`,
            opacity: Math.min(pullDistance / 80, 1),
          }}
        >
          <div className="bg-white rounded-full p-2 shadow-lg">
            {isRefreshing ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                <Camera className="w-6 h-6 text-primary" />
              </motion.div>
            ) : (
              <Camera
                className="w-6 h-6 text-primary"
                style={{
                  transform: `rotate(${pullDistance * 3.6}deg)`,
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Grid estilo Instagram Explore - Masonry dinámico con LAZY LOADING */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 auto-rows-[150px] sm:auto-rows-[200px] lg:auto-rows-[250px] gap-1 sm:gap-2"
        onTouchStart={handlePullStart}
        onTouchMove={handlePullMove}
        onTouchEnd={handlePullEnd}
      >
        <AnimatePresence>
          {/* Mostrar skeleton mientras carga */}
          {isInitialLoading &&
            [...Array(INITIAL_PHOTOS_COUNT)].map((_, index) => (
              <SkeletonPhoto
                key={`skeleton-${index}`}
                size={getPhotoSize(index)}
              />
            ))}

          {/* Mostrar SOLO fotos visibles (paginación virtual) con componente memorizado */}
          {!isInitialLoading &&
            visiblePhotos.map((photo, index) => {
              const size = getPhotoSize(index);
              const sizeClasses = getSizeClasses(size);
              // Determinar tamaño óptimo basado en conexión y posición
              const optimalSize = index < 8 ? getOptimalQuality() : "thumb";

              return (
                <PhotoItem
                  key={photo.id}
                  photo={photo}
                  index={index}
                  size={size}
                  sizeClasses={sizeClasses}
                  optimalSize={optimalSize}
                  onOpenModal={openModal}
                  onLike={handleLikeClick}
                  onComment={handleCommentClickForItem}
                  onDownload={handleDownload}
                />
              );
            })}
        </AnimatePresence>
      </div>

      {/* ===== TRIGGER PARA CARGAR MÁS (Infinite Scroll) ===== */}
      {hasMorePhotos && (
        <div
          ref={loadMoreRef}
          className="flex justify-center items-center py-8"
        >
          {isLoadingMore ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full"
            />
          ) : (
            <div className="text-center">
              <p className="text-sm text-neutral-warm mb-2">
                Mostrando {visibleCount} de {enrichedPhotos.length} fotos
              </p>
              <div className="w-8 h-8 mx-auto border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          )}
        </div>
      )}

      {/* Indicador de que se cargaron todas las fotos */}
      {!hasMorePhotos && enrichedPhotos.length > INITIAL_PHOTOS_COUNT && (
        <div className="text-center py-6">
          <p className="text-sm text-neutral-warm">
            ✨ Has visto las {enrichedPhotos.length} fotos
          </p>
        </div>
      )}

      {/* Empty state con diseño Puebla */}
      {enrichedPhotos.length === 0 && !isInitialLoading && (
        <motion.div
          className="text-center py-20 px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="p-8 bg-gradient-to-br from-accent-rose to-neutral-ivory rounded-full w-28 h-28 mx-auto mb-6 flex items-center justify-center border-4 border-accent-gold/30 shadow-elegant">
            <ImageIcon className="w-14 h-14 text-primary" />
          </div>
          <h3 className="font-serif text-3xl font-bold text-primary mb-3">
            ¡Sé el primero!
          </h3>
          <p className="font-sans text-neutral-warm text-lg max-w-md mx-auto leading-relaxed">
            Aún no hay fotos en este evento. Comparte tus mejores momentos y sé
            el primero en llenar esta galería de recuerdos.
          </p>
        </motion.div>
      )}

      {/* Modal */}
      <PhotoModal
        photo={selectedPhoto}
        isOpen={isModalOpen}
        onClose={closeModal}
        onLike={handleLikeClick}
        onNext={handleNextPhoto}
        onPrev={handlePrevPhoto}
        hasNext={currentIndex < sortedPhotos.length - 1}
        hasPrev={currentIndex > 0}
        onRequestAuth={() => {
          setShowRegisterPrompt(false);
          onRequestAuth();
        }}
      />

      {/* Modal de registro cordial */}
      <RegisterPrompt
        isOpen={showRegisterPrompt}
        onClose={() => setShowRegisterPrompt(false)}
        onRegister={() => {
          setShowRegisterPrompt(false);
          onRequestAuth();
        }}
      />

      {/* Modal de invitación a fotógrafos */}
      <PhotographerInvite
        isOpen={showPhotographerInvite}
        onClose={() => setShowPhotographerInvite(false)}
        onRegister={() => {
          setShowPhotographerInvite(false);
          onRequestAuth();
        }}
      />

      {/* Modal de comentarios */}
      <CommentModal
        isOpen={showCommentModal}
        onClose={() => {
          setShowCommentModal(false);
          setPhotoToComment(null);
        }}
        onSubmit={handleCommentSubmit}
        photo={photoToComment}
      />
    </>
  );
};

export default PhotoGallery;
